import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Cookies from 'js-cookie';
import './TwoFactorGA.css';

function TwoFactorGA() {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const { twoFactorId } = useParams();
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResendMessage('');

    setIsLoading(true);
    const encryptedEmail = Cookies.get('encryptedEmail');
    try {
      const response = await fetch(`${API_URL}/2fa/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          twoFactorId,
          code,
          encryptedEmail,
        }),
      });

      const data = await response.json();

      if (data.status === 200) {
        window.location.href = data.redirect;
      } else if (data.status === 202) {
        navigate(data.redirect);
      } else {
        setError(data.message || 'Invalid code. Please try again.');
      }
    } catch (err) {
      console.error('Error verifying 2FA:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setResendMessage('');
    setIsLoading(true);

    const encryptedEmail = Cookies.get('encryptedEmail');
    try {
      const response = await fetch(`${API_URL}/2fa/resend-sms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          twoFactorId,
          encryptedEmail,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setResendMessage('A new SMS code has been sent.');
        navigate(`/2fa/sms/${twoFactorId}`);
      } else {
        setError(data.message || 'Failed to resend SMS code. Please try again.');
      }
    } catch (err) {
      console.error('Error resending SMS code:', err);
      setError('An error occurred while resending the SMS code.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Two-Factor Authentication | SendGrid</title>
      </Helmet>
      <div className="layout" role="layout">
        <main role="page" className="layout">
          <div className="tfga-content">
            <h1 className="tfga-heading">Two-Factor Authentication</h1>
            <p className="tfga-subtext">
              Your account is protected with two-factor authentication. Please enter the 6-digit code from your Authenticator app.
            </p>
            <form onSubmit={handleSubmit} autoComplete="off">
              <div className="tfga-input-group">
                <label className="tfga-input-label" htmlFor="twoFactorCode">
                  Authenticator Code
                </label>
                <input
                  type="text"
                  id="twoFactorCode"
                  name="twoFactorCode"
                  className="tfga-input-field"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  autoFocus
                  disabled={isLoading}
                />
                {error ? (
                  <p className="tfga-error-message">{error}</p>
                ) : resendMessage ? (
                  <p className="tfga-success-message">{resendMessage}</p>
                ) : !code && !isLoading ? (
                  <p className="tfga-error-message"></p>
                ) : null}
              </div>
              <div className="tfga-button-group">
                <button
                  type="submit"
                  className={`tfga-submit-btn ${isLoading ? 'tfga-submit-btn-disabled' : ''}`}
                  disabled={!code || isLoading}
                >
                  {isLoading && (
                    <svg className="sg-loader loader-on-dark is-small" style={{ marginRight: '8px' }}>
                      <rect className="logo-square bottom-left" width="5" height="5" />
                      <rect className="logo-square bottom-middle" width="5" height="5" />
                      <rect className="logo-square middle" width="5" height="5" />
                      <rect className="logo-square top-middle" width="5" height="5" />
                      <rect className="logo-square top-right" width="5" height="5" />
                      <rect className="logo-square middle-left" width="5" height="5" />
                      <rect className="logo-square middle-right" width="5" height="5" />
                    </svg>
                  )}
                  {isLoading ? 'Verifying...' : 'Continue'}
                </button>
              </div>
            </form>
            <button
              className="tfga-resend-link"
              type="button"
              onClick={handleResend}
              disabled={isLoading}
            >
              Request code via text message instead
            </button>
          </div>
        </main>
      </div>
    </>
  );
}

export default TwoFactorGA;